import Category from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import { IGroupedFlowsForDisplay } from '@Client/runner.services/flows.formatter';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import BoxFilter, { BoxFilterColor } from '@Client/runner.shared/BoxFilter';
import FlowGroups from '@Client/runner.shared/FlowGroups/FlowGroups';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import React, { useEffect, useState } from 'react';

type Props = {
  backFromState?: string;
};

const FlowsTodo = (props: Props) => {
  const { backFromState } = props;
  const browserUtilsService = getService('browserUtilsService');
  const $state = getService('$state');
  const flowTodoService = getService('flowTodoService');
  const flowinglyMomentService = getService('flowinglyMomentService');
  const momentService = getService('momentService');
  const appInsightsService = getService('appInsightsService');
  const pubsubService = getService('pubsubService');
  const appConfig = getService('APP_CONFIG');

  const allCategoriesId = EmptyGuid;
  const allFlowModelsId = EmptyGuid;
  const isMobile = browserUtilsService.isMobileDevice();
  const restoreFilters = backFromState === STATE_MAPPINGS.flow;

  const [selectedCategoryId, setSelectedCategoryId] = useState<Guid>(
    restoreFilters ? flowTodoService.selectedCategoryId : allCategoriesId
  );
  const [selectedTagIds, setSelectedTagIds] = useState<Guid[]>(
    restoreFilters ? flowTodoService.selectedTagIds : []
  );
  const [selectedFlowModelId, setSelectedFlowModelId] = useState<Guid>(
    restoreFilters ? flowTodoService.selectedFlowModelId : allFlowModelsId
  );
  const [selectedBoxFilterId, setSelectedBoxFilterId] = useState<number>(
    restoreFilters
      ? flowTodoService.selectedBoxFilterId
      : FlowsToDoBoxFilters.ALL
  );

  const [allGroupedFlows, setAllGroupedFlows] = useState<
    IGroupedFlowsForDisplay[]
  >([]);
  const [displayedGroupedFlows, setDisplayedGroupedFlows] = useState<
    IGroupedFlowsForDisplay[]
  >([]);
  const [allFlowModels, setAllFlowModels] = useState<
    { id: Guid; name: string }[]
  >([]);
  const [categories, setCategories] = useState<
    ITreeCategory<IPublishedFlowModel>[]
  >([]);
  const [dataIsReady, setDataIsReady] = useState(false);

  const [allFlowModelTagIds, setAllFlowModelTagIds] = useState<Guid[]>([]);
  const [boxFilterAllCount, setBoxFilterAllCount] = useState(0);
  const [boxFilterDueSoonCount, setBoxFilterDueSoonCount] = useState(0);
  const [boxFilterDueTodayCount, setBoxFilterDueTodayCount] = useState(0);
  const [boxFilterOverdueCount, setBoxFilterOverdueCount] = useState(0);

  const emptyPageTitle = "Hooray, you're up to date!";
  const emptyPageText = `There are no steps requiring action from you right now
   that match your filter criteria.<br />
   We'll notify you when there are. 
   You can see the status of Flows that you are currently in by clicking 'Flows I'm in'.`;
  const emptyPageButtonLabel = "See Flows I'm In";

  const boxFilterChanged = (newBoxFilterId: FlowsToDoBoxFilters) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      newBoxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );
    setSelectedBoxFilterId(newBoxFilterId);
    flowTodoService.selectedBoxFilterId = newBoxFilterId;
  };

  const boxFilters = [
    {
      id: FlowsToDoBoxFilters.ALL,
      title: 'All',
      toolTip: 'All flows waiting on you',
      count: boxFilterAllCount,
      color: BoxFilterColor.TEAL,
      onClick: boxFilterChanged,
      selected: selectedBoxFilterId === FlowsToDoBoxFilters.ALL
    },
    {
      id: FlowsToDoBoxFilters.DUE_SOON,
      title: 'Due soon',
      toolTip: 'Flows waiting on you that are due soon',
      count: boxFilterDueSoonCount,
      color: BoxFilterColor.BLACK,
      onClick: boxFilterChanged,
      selected: selectedBoxFilterId === FlowsToDoBoxFilters.DUE_SOON
    },
    {
      id: FlowsToDoBoxFilters.DUE_TODAY,
      title: 'Due today',
      toolTip: 'Flows waiting on you that are due today',
      count: boxFilterDueTodayCount,
      color: BoxFilterColor.BLACK,
      onClick: boxFilterChanged,
      selected: selectedBoxFilterId === FlowsToDoBoxFilters.DUE_TODAY
    },
    {
      id: FlowsToDoBoxFilters.OVERDUE,
      title: 'Overdue',
      toolTip: 'Flows waiting on you that are overdue',
      count: boxFilterOverdueCount,
      color: BoxFilterColor.ORANGE,
      onClick: boxFilterChanged,
      selected: selectedBoxFilterId === FlowsToDoBoxFilters.OVERDUE
    }
  ];

  const getUpdatedFlows = () => {
    flowTodoService.getFlows().then((groupedFlows) => {
      setNewGroupedFlows(groupedFlows);
    });
  };

  const subscriberId = 'flowingly.runner.flows.todo';
  pubsubService.unsubscribeAll(subscriberId);
  pubsubService.subscribeManyLate(
    [
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      'SIGNALR_RUNNER_STEP_WEBHOOK_UPDATED',
      'SIGNALR_RUNNER_START_FLOW',
      'SIGNALR_RUNNER_COMPLETE_STEP',
      'SIGNALR_RUNNER_REASSIGN_STEP',
      'SIGNALR_RUNNER_WITHDRAW_FLOW',
      'SIGNALR_ACTOR_DELETED',
      'SIGNALR_RUNNER_USER_TEAM_UPDATED',
      'SIGNALR_RUNNER_NEW_FLOW_COMMENT_COUNT',
      'SIGNALR_RUNNER_STEP_TASK_CANCELLED',
      'SIGNALR_RUNNER_STEP_TASK_CREATED',
      'SIGNALR_RUNNER_STEP_TASK_UPDATED',
      'SIGNALR_RUNNER_STEP_INTEGRATION_PROCESSING_UPDATED'
    ],
    getUpdatedFlows,
    subscriberId
  );

  useEffect(() => {
    flowTodoService.getFlows().then((groupedFlows) => {
      setNewGroupedFlows(groupedFlows);
      appInsightsService.trackMetricIfTimerExist('flowsTodoEntered');
    });

    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const setNewGroupedFlows = (newGroups: IGroupedFlowsForDisplay[]) => {
    setAllGroupedFlows(newGroups);
    updateDisplayedFlowModels(
      newGroups,
      selectedCategoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );

    const newTagIds = new Set<Guid>();
    newGroups.forEach((group) => {
      group.flows.forEach((flow) => {
        flow.parsedTags.forEach((tag) => newTagIds.add(tag.id));
      });
    });
    setAllFlowModelTagIds(Array.from(newTagIds));
    const validTagIds = selectedTagIds.filter((id) => newTagIds.has(id));
    setSelectedTagIds(validTagIds);
    flowTodoService.selectedTagIds = validTagIds;

    const newCategories = generateCategories(newGroups);
    setCategories(newCategories);
    const findSelectedCategory = (current: ITreeCategory<any>) => {
      if (current.id === selectedCategoryId) {
        return true;
      }
      return current.items.some(findSelectedCategory);
    };
    if (!newCategories.some(findSelectedCategory)) {
      setSelectedCategoryId(allCategoriesId);
      flowTodoService.selectedCategoryId = allCategoriesId;
    }

    setDataIsReady(true);
  };

  const isFlowDueSoon = (dueDate: string) => {
    return dueDate && !flowinglyMomentService.isOverdue(dueDate);
  };

  const isFlowDueToday = (dueDate: string, todayDate: string) => {
    return (
      dueDate &&
      !flowinglyMomentService.isOverdue(dueDate) &&
      momentService
        .utc(dueDate, 'YYYY-MM-DD HH:mm:ss')
        .local()
        .format('YYYY-MM-DD') === todayDate
    );
  };

  const isFlowOverdue = (dueDate: string) => {
    return flowinglyMomentService.isOverdue(dueDate);
  };

  const generateCategories = (groupedFlows: IGroupedFlowsForDisplay[]) => {
    const categories = [] as ITreeCategory<IPublishedFlowModel>[];
    for (const flowModel of groupedFlows) {
      let categoryTree = flowModel.categoryTree;
      let currentCategories = categories;
      while (categoryTree) {
        let category = currentCategories.find(
          (cat) => cat.id === categoryTree.categoryId
        );
        if (!category) {
          category = {
            id: categoryTree.categoryId,
            text: categoryTree.categoryName,
            flowModels: [],
            items: [],
            count: 0
          };
          currentCategories.push(category);
        }
        currentCategories = category.items;
        categoryTree = categoryTree.category;
        if (!categoryTree) {
          category.count += flowModel.flows.length;
        }
      }
    }

    categories.sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );

    categories.splice(0, 0, {
      id: allCategoriesId,
      text: 'All',
      flowModels: [],
      items: []
    });

    return categories;
  };

  const selectCategory = (categoryId: Guid) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      categoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );
    setSelectedCategoryId(categoryId);
    flowTodoService.selectedCategoryId = categoryId;
  };

  const selectTagFilters = (tagIds: Guid[]) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      tagIds
    );
    setSelectedTagIds(tagIds);
    flowTodoService.selectedTagIds = tagIds;
  };

  const selectFlowModelFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFlowModelId = e.target.value as Guid;
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      selectedBoxFilterId,
      newFlowModelId,
      selectedTagIds
    );
    setSelectedFlowModelId(newFlowModelId);
    flowTodoService.selectedFlowModelId = newFlowModelId;
  };

  const updateDisplayedFlowModels = (
    groups: IGroupedFlowsForDisplay[],
    categoryId: Guid,
    boxFilterId: FlowsToDoBoxFilters,
    flowModelId: Guid,
    filterTagIds: Guid[]
  ) => {
    let filteredGroups = groups;
    filteredGroups.forEach((group) => {
      group.flows.forEach((flow) => {
        flow.show = true;
      });
    });
    if (categoryId !== allCategoriesId) {
      filteredGroups = filteredGroups.filter(
        (group) => group.categoryId === categoryId
      );
    }

    const newFlowModels = [
      { id: allFlowModelsId, name: 'All Flows' },
      ...filteredGroups.map((g) => {
        return { id: g.flowModelId, name: g.groupName };
      })
    ];
    setAllFlowModels(newFlowModels);
    if (!newFlowModels.some((m) => m.id === selectedFlowModelId)) {
      setSelectedFlowModelId(allFlowModelsId);
      flowTodoService.selectedFlowModelId = allFlowModelsId;
      flowModelId = allFlowModelsId;
    }

    if (filterTagIds.length > 0) {
      filteredGroups = filteredGroups.filter((group) => {
        let groupIncludesTag = false;
        group.flows.forEach((flow) => {
          flow.show = flow.parsedTags.some((flowTag) => {
            if (filterTagIds.includes(flowTag.id)) {
              groupIncludesTag = true;
              return true;
            }
            return false;
          });
        });
        return groupIncludesTag;
      });
    }

    if (flowModelId !== allFlowModelsId) {
      filteredGroups = filteredGroups.filter(
        (group) => group.flowModelId === flowModelId
      );
    }

    setBoxFilterAllCount(
      filteredGroups.reduce(
        (acc, group) => acc + group.flows.filter((f) => f.show).length,
        0
      )
    );

    const dueSoonCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowDueSoon(flow.dueDate))
          .length,
      0
    );
    setBoxFilterDueSoonCount(dueSoonCount);

    const todayDateString = momentService(new Date())
      .local()
      .format('YYYY-MM-DD');
    const dueTodayCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter(
          (flow) => flow.show && isFlowDueToday(flow.dueDate, todayDateString)
        ).length,
      0
    );
    setBoxFilterDueTodayCount(dueTodayCount);

    const overdueCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowOverdue(flow.dueDate))
          .length,
      0
    );
    setBoxFilterOverdueCount(overdueCount);

    if (boxFilterId === FlowsToDoBoxFilters.ALL) {
      // Don't filter
    } else if (boxFilterId === FlowsToDoBoxFilters.DUE_SOON) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowDueSoon(flow.dueDate);
        }
      }
    } else if (boxFilterId === FlowsToDoBoxFilters.DUE_TODAY) {
      const todayDateString = momentService(new Date())
        .local()
        .format('YYYY-MM-DD');
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show =
            flow.show && isFlowDueToday(flow.dueDate, todayDateString);
        }
      }
    } else if (boxFilterId === FlowsToDoBoxFilters.OVERDUE) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowOverdue(flow.dueDate);
        }
      }
    }

    filteredGroups = filteredGroups.filter((group) =>
      group.flows.some((flow) => flow.show)
    );

    setDisplayedGroupedFlows(filteredGroups);
  };

  return (
    <div id="flows-todo" className="runner-page">
      <RunnerPageDescription
        showText={!isMobile}
        pageHeaderText="To Do"
        pageDescription="A list of flows waiting on you to complete a step."
        helpLink="https://help.flowingly.net/getting-started/how-do-i-complete-a-step"
      />
      {isMobile && dataIsReady && (
        <div className="flows-todo-content-mobile">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
            defaultItemId={allCategoriesId}
          />
          <select
            id="flows-todo-flow-model-select"
            title="Filter by Flow Model"
            onChange={selectFlowModelFilter}
          >
            {allFlowModels.map((model) => (
              <option key={model.id} value={model.id} title={model.name}>
                {model.name.substring(0, appConfig.flowFilterMaxCharacters)}
              </option>
            ))}
          </select>
          <FlowGroups
            isMobile={isMobile}
            groups={displayedGroupedFlows}
            onFlowClick={(flowId: Guid) => {
              $state.go(STATE_MAPPINGS.flow, { flowId });
            }}
          ></FlowGroups>
          {displayedGroupedFlows.length === 0 && (
            <EmptyState
              summaryText={emptyPageTitle}
              bodyText={emptyPageText}
              buttonText={emptyPageButtonLabel}
              onButtonClick={() => $state.go(STATE_MAPPINGS.flowsin)}
            />
          )}
        </div>
      )}
      {!isMobile && dataIsReady && (
        <div className="flows-todo-content runner-page__content">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
            defaultItemId={allCategoriesId}
          />
          <div className="flows-todo-list runner-page__content__main-wrapper">
            <div className="runner-page__content__main-content">
              <div className="toolbar">
                <div className="flows-todo-list-toolbar__dropdowns">
                  <select
                    id="flows-todo-flow-model-select"
                    title="Filter by Flow Model"
                    onChange={selectFlowModelFilter}
                    value={selectedFlowModelId}
                  >
                    {allFlowModels.map((model) => (
                      <option
                        key={model.id}
                        value={model.id}
                        title={model.name}
                      >
                        {model.name.substring(
                          0,
                          appConfig.flowFilterMaxCharacters
                        )}
                      </option>
                    ))}
                  </select>
                  {appConfig.enableTagging && (
                    <TagFilter
                      usedTagIds={allFlowModelTagIds}
                      initialSelectedTagIds={selectedTagIds}
                      onApply={selectTagFilters}
                    ></TagFilter>
                  )}
                </div>
                {appConfig.showDashboardsV1 && (
                  <div className="box-filters">
                    {boxFilters.map((filter) => (
                      <BoxFilter
                        key={filter.id}
                        title={filter.title}
                        tooltip={filter.toolTip}
                        count={filter.count}
                        color={filter.color}
                        onClick={() => filter.onClick(filter.id)}
                        selected={filter.selected}
                      />
                    ))}
                  </div>
                )}
              </div>
              <FlowGroups
                isMobile={isMobile}
                groups={displayedGroupedFlows}
                onFlowClick={(flowId: Guid) => {
                  $state.go(STATE_MAPPINGS.flow, { flowId });
                }}
              ></FlowGroups>
              {displayedGroupedFlows.length === 0 && (
                <EmptyState
                  summaryText={emptyPageTitle}
                  bodyText={emptyPageText}
                  buttonText={emptyPageButtonLabel}
                  onButtonClick={() => $state.go(STATE_MAPPINGS.flowsin)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsTodo;

export enum FlowsToDoBoxFilters {
  ALL = 0,
  DUE_SOON = 1,
  DUE_TODAY = 2,
  OVERDUE = 3
}
